<template>
  <div class="image-tab">
    <div class="required-line">
      <span class="status-label">Cette publication a</span>
      <div class="chips" role="group" aria-label="Image de la publication">
        <button type="button" class="chip" :class="{selected: !required}" :aria-pressed="!required" @click="setRequired(false)">Pas d'image</button>
        <button type="button" class="chip" :class="{selected: required}" :aria-pressed="required" @click="setRequired(true)">Une image</button>
      </div>
    </div>

    <p v-if="!required" class="hint">Sans image, le post part en texte seul. L'image reste possible plus tard : choisissez « Une image ».</p>

    <div v-else class="columns image-columns">
      <div class="column">
        <CustomTextAreaField :content=inputs.imageBrief :auto-grow="true" :min-height="120" />
        <p class="hint">Ce que l'image doit montrer : titre court, visuel ou chiffres. Utile pour la demander à l'outil qui la crée.</p>
        <div class="status-line">
          <span class="status-label">L'image est</span>
          <div class="chips" role="group" aria-label="État de l'image">
            <button v-for="status in imageStatuses" :key="status.value" type="button" class="chip" :class="{selected: publication.image.status === status.value}"
                    :aria-pressed="publication.image.status === status.value" @click="setStatus(status.value)">{{ status.label }}</button>
          </div>
        </div>
        <button v-if="inputs.imageBrief.value" type="button" class="panel-button copy" @click="copyBrief">Copier la description</button>
      </div>

      <div class="column">
        <h3 class="panel-title">Fichiers ({{ files.length }}/{{ maxImages }})</h3>
        <ul v-if="files.length > 0" class="files">
          <li v-for="file in files" :key="file.id">
            <img v-if="previews[file.id]" :src="previews[file.id]" :alt="file.name" class="thumb" />
            <span v-else class="thumb placeholder" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="M21 16l-5-5-8 9"/></svg>
            </span>
            <span class="file-info"><span class="file-name" :title="file.name">{{ file.name }}</span><span class="file-size">{{ formatSize(file.size) }}</span></span>
            <button type="button" class="check-remove" title="Retirer cette image" @click="remove(file)"><span class="fas fa-trash-can"></span></button>
          </li>
        </ul>
        <div v-if="files.length < maxImages" class="dropzone" :class="{over: dragging, busy: uploading}"
             @dragover.prevent="dragging = true" @dragleave.prevent="dragging = false" @drop.prevent="drop">
          <span class="fas fa-cloud-arrow-up"></span>
          <span>{{ uploading ? 'Envoi en cours…' : 'Déposez l\'image ici ou' }}</span>
          <button type="button" class="panel-button" :disabled="uploading" @click="$refs.fileInput.click()">Choisir un fichier</button>
        </div>
        <input ref="fileInput" type="file" class="file-input" accept="image/png,image/jpeg,image/webp" @change="pick" />
        <p class="hint">PNG, JPEG ou WebP, 10 Mo au plus.<span v-if="maxImages === 1"> Un post LinkedIn n'a qu'une seule image.</span></p>
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCloudArrowUp, faImage, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import CustomTextAreaField from "@/components/forms/customs/customTextAreaField/CustomTextAreaField.vue";
import {NotificationService} from "@vues/v-common";
import {AssetController} from "@/marketing/controllers/AssetController";
import {CHANNEL_ENUM, IMAGE_STATUS_ENUM, copyText} from "@/marketing/utils";
library.add(faCloudArrowUp, faImage, faTrashCan);

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_SIZE = 10 * 1024 * 1024;

/**
 * Onglet Image : image demandée ou non, description pour l'outil qui la crée, état, fichiers.
 * setRequired(bool), setStatus(statut), upload(fichier) et removeFile(id) sont portés par la modale : ils enregistrent tout de suite.
 */
export default {
  name: 'PublicationImage',
  props: ['publication', 'inputs', 'setRequired', 'setStatus', 'upload', 'removeFile'],
  components: {CustomTextAreaField},
  data() {
    return {
      previews: {},
      dragging: false,
      uploading: false,
      imageStatuses: [IMAGE_STATUS_ENUM.TO_DO, IMAGE_STATUS_ENUM.GENERATED, IMAGE_STATUS_ENUM.VALIDATED],
    };
  },
  computed: {
    required() {
      return this.publication.image?.required === true;
    },
    files() {
      return this.publication.image?.files ?? [];
    },
    // Un post LinkedIn n'a qu'une image, un carrousel Instagram en a plusieurs
    maxImages() {
      return Object.values(CHANNEL_ENUM).find(channel => channel.value === this.publication.channel)?.maxImages ?? 1;
    },
  },
  watch: {
    // Les aperçus suivent la liste des fichiers
    files: {
      handler() {
        this.loadPreviews();
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    Object.values(this.previews).forEach(url => URL.revokeObjectURL(url));
  },
  methods: {
    formatSize(size) {
      return size >= 1024 * 1024 ? `${(size / 1024 / 1024).toLocaleString('fr-FR', {maximumFractionDigits: 1})} Mo` : `${Math.max(1, Math.round(size / 1024))} Ko`;
    },
    async loadPreviews() {
      const ids = this.files.map(file => file.id);
      Object.keys(this.previews).filter(id => !ids.includes(id)).forEach(id => {
        URL.revokeObjectURL(this.previews[id]);
        delete this.previews[id];
      });
      for (const file of this.files) {
        if (this.previews[file.id]) continue;
        const blob = await AssetController.download(this.publication.id, file.id);
        // Une erreur du serveur arrive en JSON, pas en image
        if (blob instanceof Blob && !blob.type.includes("json")) this.previews[file.id] = URL.createObjectURL(blob);
      }
    },
    async send(file) {
      if (!file) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        (new NotificationService).addNotification("danger", "Format non pris en charge : PNG, JPEG ou WebP");
        return;
      }
      if (file.size > MAX_SIZE) {
        (new NotificationService).addNotification("danger", "L'image dépasse 10 Mo");
        return;
      }
      this.uploading = true;
      await this.upload(file);
      this.uploading = false;
    },
    pick(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      return this.send(file);
    },
    drop(event) {
      this.dragging = false;
      return this.send(event.dataTransfer?.files?.[0]);
    },
    remove(file) {
      return this.removeFile(file);
    },
    async copyBrief() {
      const copied = await copyText(this.inputs.imageBrief.value);
      (new NotificationService).addNotification(copied ? "success" : "danger", copied ? "Description copiée" : "Impossible de copier");
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/marketing/style/modal.styl';

.column
  min-width 0

.required-line, .status-line
  display flex
  align-items center
  flex-wrap wrap
  gap 10px
  margin-bottom 6px

.status-label
  color CTRL_LABEL
  font-size 0.85em
  font-weight 600

.copy
  margin-top 10px
  height 34px

.files
  margin 0 0 10px
  padding 0
  list-style none

  li
    display flex
    align-items center
    gap 12px
    padding 6px 2px
    border-bottom 1px solid PAGE_RULE_SOFT

.thumb
  flex 0 0 auto
  width 88px
  height 66px
  border-radius 8px
  background CTRL_BG
  object-fit contain

  &.placeholder
    display flex
    align-items center
    justify-content center
    color CTRL_TEXT_MUTED

.file-info
  display flex
  flex-direction column
  min-width 0
  flex 1 1 auto

.file-name
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  font-weight 600

.file-size
  color CTRL_TEXT_MUTED
  font-size 0.85em

.check-remove
  padding 4px 8px
  border 0
  background none
  color CTRL_TEXT_MUTED
  cursor pointer

  &:hover
    color CTRL_ERROR

.dropzone
  display flex
  align-items center
  justify-content center
  flex-wrap wrap
  gap 10px
  box-sizing border-box
  padding 18px 14px
  border 2px dashed CTRL_BORDER_HOVER
  border-radius CTRL_RADIUS_LG
  background CTRL_BG
  color CTRL_TEXT_MUTED
  font-size 0.9em
  transition background-color CTRL_EASE, border-color CTRL_EASE

  &.over
    border-color THEME_COLOR_PRIMARY
    background PAGE_LEMON_HOVER

  &.busy
    opacity 0.7

.file-input
  display none
</style>
